import { endOfWeek, startOfWeek } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { type DateRange } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useWorkerShifts } from "@src/appV2/OpenShifts/api/useWorkerShifts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { addDays, isValid, parseISO, subDays } from "date-fns";

import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../../../ShiftDiscovery/Filters/constants";
import { useGetOpenShifts } from "../../Open/useGetOpenShifts";
import { type HoursRestrictionConflictData } from "../types";

export interface UseHourRestrictionConflictDataProps {
  shiftStart?: string;
  workplaceId?: string;
}

export interface UseHourRestrictionConflictDataOptions {
  enabled?: boolean;
}

/**
 * iOS 14 has a bug where the startOfWeek and endOfWeek functions return invalid dates.
 * This is a fallback to disable this hook on iOS 14.
 */
function tryCreateValidRangeForTheWeek(
  validatedShiftDate: Date,
  workerTmz: string
): { dateRange: DateRange; isValid: boolean } {
  const bookedShiftsDateRange: DateRange = {
    startDate: subDays(startOfWeek(validatedShiftDate, { timeZone: workerTmz }), 1),
    endDate: addDays(endOfWeek(validatedShiftDate, { timeZone: workerTmz }), 1),
  };

  if (isValid(bookedShiftsDateRange.startDate) && isValid(bookedShiftsDateRange.endDate)) {
    return {
      dateRange: bookedShiftsDateRange,
      isValid: true,
    };
  }

  return {
    dateRange: {
      startDate: new Date(),
      endDate: new Date(),
    },
    isValid: false,
  };
}

export function useHourRestrictionConflictData(
  props: UseHourRestrictionConflictDataProps,
  options: UseHourRestrictionConflictDataOptions = {}
) {
  const { workplaceId, shiftStart } = props;
  const { enabled } = options;

  const worker = useDefinedWorker();
  const workerGeoLocation = useWorkerGeoLocation();

  const shiftDate = isDefined(shiftStart) ? parseISO(shiftStart) : new Date();
  const validatedShiftDate = isValid(shiftDate) ? shiftDate : new Date();

  if (!isValid(shiftDate)) {
    logError("Invalid shift start date", {
      error: new Error("Invalid shift start date"),
      metadata: {
        shiftStart,
        shiftDate,
      },
    });
  }

  const { dateRange: bookedShiftsDateRange, isValid: isBookedShiftsDateRangeValid } =
    tryCreateValidRangeForTheWeek(validatedShiftDate, worker.tmz);

  if (enabled && !isBookedShiftsDateRangeValid) {
    logEvent(APP_V2_APP_EVENTS.HOUR_RESTRICTION_CONFLICT_CHECK_DATE_RANGE_ERROR, {
      metadata: {
        worker: worker.userId,
        shiftStart,
        workplaceId,
      },
    });
  }

  // If the query is disabled, 'isLoading' is true until the query runs and we have data.
  // This can cause the booking button to be stuck in a loading state when enable conditions
  // aren't met (e.g., workplaceId not yet resolved after app backgrounding). Therefore,
  // we need to use isInitialLoading instead.
  // https://tanstack.com/query/v4/docs/framework/react/guides/disabling-queries#isinitialloading
  const {
    data: openShiftsAtSameFacilityResponse,
    isError: openShiftsAtSameFacilityIsError,
    isInitialLoading: openShiftsAtSameFacilityIsInitialLoading,
  } = useGetOpenShifts(
    {
      filter: {
        "workplace.id": workplaceId,
        start: {
          gte: bookedShiftsDateRange.startDate.toISOString(),
          lte: bookedShiftsDateRange.endDate.toISOString(),
        },
        area: {
          latitude: workerGeoLocation?.latitude ?? 0,
          longitude: workerGeoLocation?.longitude ?? 0,
          radiusInMiles: DEFAULT_DISTANCE_IN_MILES_FILTER,
        },
      },
    },
    {
      enabled:
        enabled && isDefined(shiftStart) && isDefined(workplaceId) && isBookedShiftsDateRangeValid,
    }
  );

  const {
    data: agentShiftsData,
    isInitialLoading: agentShiftsIsInitialLoading,
    isError: agentShiftsIsError,
  } = useWorkerShifts(
    {
      startDate: bookedShiftsDateRange.startDate,
      endDate: bookedShiftsDateRange.endDate,
      tmz: worker.tmz,
      groupByDate: false,
    },
    {
      enabled: enabled && isDefined(shiftStart) && isBookedShiftsDateRangeValid,
    }
  );

  const bookedShiftsAtSameFacility = agentShiftsData?.agentShifts.filter(
    (s) => s.facilityId === workplaceId
  );

  const isLoading = openShiftsAtSameFacilityIsInitialLoading || agentShiftsIsInitialLoading;

  let data: HoursRestrictionConflictData | undefined;

  if (
    !isLoading &&
    isDefined(openShiftsAtSameFacilityResponse) &&
    isDefined(bookedShiftsAtSameFacility)
  ) {
    data = {
      openShiftsAtSameFacility: openShiftsAtSameFacilityResponse?.data.map((openShift) => ({
        id: openShift.id,
        start: openShift.attributes.start,
        end: openShift.attributes.end,
      })),
      bookedShiftsAtSameFacility: bookedShiftsAtSameFacility.map((shift) => ({
        id: String(shift.shiftId),
        start: shift.start,
        end: shift.end,
      })),
    };
  }

  return {
    data,
    isLoading,
    isError: openShiftsAtSameFacilityIsError || agentShiftsIsError,
  };
}
